"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Bot, FileText, CheckCircle2, ShieldAlert, ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
export default function AiBuyerResearch() {
  const shouldReduceMotion = useReducedMotion();
  const points = [{
    title: "Structured Information Architecture",
    description: "Service and solution pages clearly structured with concise summaries so AI-assisted retrieval tools can understand and digest offerings accurately.",
    icon: FileText
  }, {
    title: "Authoritative Service Descriptions",
    description: "Accurate, unambiguous explanations of services, industries and use cases that answer specific technical, commercial and evaluation questions.",
    icon: CheckCircle2
  }, {
    title: "Genuine Technical & Topical Authority",
    description: "Authentic subject expertise, clean entity signals and trustworthy content that assist visibility across both traditional and AI-assisted search environments.",
    icon: Bot
  }];
  return <section id="b2baisearch" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 px-4 sm:px-6 lg:px-8 border-b border-border transition-colors duration-200">
      <div className="max-w-7xl mx-auto">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <Badge variant="outline" className="mb-4 px-3 py-1 border-primary/30 text-primary font-medium rounded-full">
            Modern Search Landscape
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight font-default text-foreground">
            B2B SEO and AI-assisted buyer research
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            B2B buyers increasingly use AI-assisted tools such as Google AI Overviews and ChatGPT to research suppliers and compare solutions. Preparing your digital presence for this shift requires clear structure, factual precision and genuine topical depth.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {points.map((item, index) => {
          const Icon = item.icon;
          return <motion.div key={item.title} initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.35,
            delay: index * 0.1
          }}>
                <Card className="h-full border border-border/80 bg-card hover:border-primary/40 transition-all shadow-xs">
                  <CardHeader className="pb-3">
                    <div className="size-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-3">
                      <Icon className="size-5" />
                    </div>
                    <CardTitle className="text-lg font-semibold text-foreground">
                      {item.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {item.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </div>

        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.35,
        delay: 0.3
      }} className="max-w-3xl mx-auto">
          <Card className="border border-border bg-muted/40 shadow-xs">
            <CardContent className="p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-5">
              <div className="size-10 rounded-full bg-secondary flex items-center justify-center text-primary shrink-0">
                <ShieldAlert className="size-5" />
              </div>
              <div className="space-y-2 text-left">
                <p className="text-sm sm:text-base text-foreground leading-relaxed">
                  I align B2B SEO with{" "}
                  <Link to="/AISearchOptimisationPage" className="text-primary underline underline-offset-4 hover:text-primary/80 font-medium inline-flex items-center gap-1">
                    AI Search Optimisation
                    <ArrowRight className="size-3.5" />
                  </Link>{" "}
                  where it supports your commercial goals, but I will not promise your site will be cited or recommended by AI systems.
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>;
}